import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RiHistoryLine, RiTimeLine, RiFileTextLine, RiArrowRightLine,
  RiLayoutGridLine, RiInboxUnarchiveLine,
} from 'react-icons/ri';
import { getPracticeTests, startTest } from '../../api/practiceTests.api';
import { C, LEVEL_COLORS } from '../../data/constants';
import { LoadingScreen, EmptyState } from '../../components/shared/UI';
import { useAppConfig } from '../../context/ConfigContext';
import { useAppLevel } from '../../components/app/AppTopbar';

const LEVELS = ['N5', 'N4', 'N3', 'N2', 'N1'];

export default function PracticeTests() {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { level } = useAppLevel();
  const [filter, setFilter] = useState(level);
  const [startingId, setStartingId] = useState(null);
  const { config } = useAppConfig();
  const liveLevels = LEVELS.filter(l => config.levels?.[l] !== false);

  const loadTests = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getPracticeTests({ level: filter });
      setTests(res.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  // Changing the level in the topbar re-points this page at that level.
  useEffect(() => { setFilter(level); }, [level]);

  useEffect(() => { loadTests(); }, [loadTests]);

  const handleStart = async (testId) => {
    setStartingId(testId);
    try {
      await startTest(testId);
      navigate(`/practice-tests/${testId}/take`);
    } catch (err) {
      setError(err.message);
      setStartingId(null);
    }
  };

  return (
    <div className="page-container ptest-page anim-fade-up">
      <div className="ptest-head">
        <div className="ptest-head-copy">
          <h1>Practice Tests</h1>
          <p>Full mock exams under real timing — the closest thing to sitting the JLPT.</p>
        </div>
        <button className="btn-ghost ptest-history-btn" onClick={() => navigate('/practice-tests/history')}>
          <RiHistoryLine size={16} /> View history
        </button>
      </div>

      <div className="ptest-levels">
        {/* `lvl`, not `level` — the outer `level` is the app-wide choice
            from the topbar and shadowing it here reads as a bug. */}
        {liveLevels.map(lvl => (
          <button
            key={lvl}
            type="button"
            className={`ptest-level${filter === lvl ? ' active' : ''}`}
            style={{ '--lvl-color': LEVEL_COLORS[lvl] || C.accent }}
            onClick={() => setFilter(lvl)}
          >
            {lvl}
          </button>
        ))}
      </div>

      {error && <div className="ptest-error">{error}</div>}

      {loading ? (
        <LoadingScreen label="Loading practice tests" />
      ) : tests.length === 0 ? (
        <div className="ks-card">
          <EmptyState
            icon={RiInboxUnarchiveLine}
            title={`No ${filter} tests yet`}
            sub={filter === 'N5'
              ? 'Practice tests are added over time — check back soon.'
              : `No ${filter} tests have been added yet.`}
          />
        </div>
      ) : (
        <div className="ptest-grid">
          {tests.map(test => {
            const color = LEVEL_COLORS[test.level] || C.accent;
            const totalQuestions = (test.sections || []).reduce(
              (sum, s) => sum + (s.questions?.length || 0), 0
            );
            return (
              <article key={test._id} className="ks-card ptest-card" style={{ '--lvl-color': color }}>
                <header className="ptest-card-head">
                  <span className="ptest-card-level">{test.level}</span>
                  <div className="ptest-card-title">
                    <h2>{test.title}</h2>
                    {test.description && <p>{test.description}</p>}
                  </div>
                </header>

                <div className="ptest-card-stats">
                  <span><RiTimeLine size={14} /> {test.totalDuration} min</span>
                  <span><RiLayoutGridLine size={14} /> {test.sections?.length || 0} sections</span>
                  <span><RiFileTextLine size={14} /> {totalQuestions} questions</span>
                </div>

                {!!test.sections?.length && (
                  <ul className="ptest-sections">
                    {test.sections.map(section => (
                      <li key={section.name}>
                        <span>{section.displayName}</span>
                        <em>{section.questions?.length || 0} Q</em>
                      </li>
                    ))}
                  </ul>
                )}

                <button
                  className="btn-primary ptest-start"
                  onClick={() => handleStart(test._id)}
                  disabled={startingId === test._id}
                >
                  {startingId === test._id ? <div className="spinner" /> : <>Start test <RiArrowRightLine size={15} /></>}
                </button>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
